import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalSizes,
  ButtonVariants,
  Stepper,
  Table,
  Alert,
  AlertVariants,
  Dropdown,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader, resolveRowUnitType } from '@/hooks';
import { DiscrepancyModal } from '../Discrepancy';
import { SummaryCard } from '../SummaryCard';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildTableLabels } from '@/config/tableLabels';
import { buildContractFilterDefinitions } from '@/config/filterDefinitions';
import { shipmentUnits, contractUnits } from './linkUnits';
import { assignedTotalInUnit } from './linkFields';
import {
  buildLinkContractsStep1Columns,
  buildLinkContractsStep2Columns,
  buildLinkShipmentsStep2Columns,
} from './linkContractsColumns';
import {
  buildContractRowDiscrepancySections,
  buildShipmentRowDiscrepancySections,
} from './linkContractsDiscrepancy';
import { EMPTY_LIST } from '@/lib/utils';
import { buildSuggestionGroups, getSuggestionGroup } from '../pickerGroups';
import styles from './LinkContractsModal.module.scss';

// Which way round the link goes; it decides the step-1 picker and the step-2 rows.
export const LINK_CONTRACT_FLOWS = Object.freeze({
  oneShipment: 'oneShipment',
  manyShipments: 'manyShipments',
});

const SELECT_STEP = 0;
const ASSIGN_STEP = 1;
const getRowId = (row) => row.id;

/**
 * Props:
 *   open          whether the modal is shown
 *   onClose       close handler (✕ / Cancel / Escape)
 *   title         the modal's heading, already carrying the shipment id
 *   detailFields  (unitType, assignedInUnit) => GENERAL DETAILS card rows, so the card
 *                 reads in the unit the dropdown below it is on; `assignedInUnit(unitType)`
 *                 is step 2's running total, which an Outstanding quantity row deducts
 *   contracts     linkable-contract row view models
 *   loading       whether the contract list is still fetching
 *   flow          one of LINK_CONTRACT_FLOWS — many contracts onto one shipment, or one
 *                 contract onto the many selected shipments
 *   shipments     (manyShipments) the selected shipment rows shown on step 2
 *   unassignedByUnit  (oneShipment) the shipment's still-unassigned amounts, raw, per unit
 *   isSaving      disables Save while the mutation is in flight
 *   onSearchChange (term) => void — the list is filtered server-side
 *   onSave        (entries, pickedContractId) => void — entries are
 *                 `{ rowId, quantity, unitSymbol }`, the unit being the row's own
 */
export const LinkContractsModal = ({
  open,
  onClose,
  title,
  detailFields,
  contracts = EMPTY_LIST,
  loading = false,
  flow = LINK_CONTRACT_FLOWS.oneShipment,
  shipments = EMPTY_LIST,
  unassignedByUnit,
  isSaving = false,
  onSearchChange,
  onFilterChange,
  onSave,
}) => {
  const t = useTranslations();
  const isBulk = flow === LINK_CONTRACT_FLOWS.manyShipments;
  const [step, setStep] = useState(SELECT_STEP);
  const [selectedIds, setSelectedIds] = useState(EMPTY_LIST);
  const [pickedId, setPickedId] = useState(null);
  const [assignments, setAssignments] = useState({});
  // The Table's key — it owns its search, selection and page, so only a remount clears them.
  const [session, setSession] = useState(0);
  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  const [error, setError] = useState(null);

  // Step 2's rows: the selected shipments, or the contracts picked on step 1.
  const step1Rows = contracts;
  const step2Rows = useMemo(() => {
    if (isBulk) return shipments.filter((row) => assignments[row.id] !== undefined);
    return contracts.filter((row) => selectedIds.includes(row.id));
  }, [isBulk, shipments, contracts, selectedIds, assignments]);

  const unitRows = step === SELECT_STEP ? step1Rows : step2Rows;
  const unitOptions =
    step === ASSIGN_STEP && isBulk
      ? shipmentUnits.buildUnitHeaderOptions
      : contractUnits.buildUnitHeaderOptions;
  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    unitRows,
    unitOptions,
    t,
  );

  // The builder asks in the basis it renders, which is not always the header's.
  const assignedInUnit = useCallback(
    (unit) => assignedTotalInUnit(step2Rows, assignments, selections, unit),
    [step2Rows, assignments, selections],
  );

  // Each cell's ceiling also counts what is left of the shipment once the other rows are typed.
  const assignRows = useMemo(() => {
    if (!unassignedByUnit) return step2Rows;
    return step2Rows.map((row) => {
      const unitType = resolveRowUnitType(row, selections);
      const shipmentCap = unassignedByUnit[unitType];
      if (shipmentCap == null) return row;
      const own = Number(assignments[row.id]);
      const left = shipmentCap - (assignedInUnit(unitType) - (Number.isFinite(own) ? own : 0));
      const rowCap = row.unassignedByUnit?.[unitType];
      return {
        ...row,
        unassignedByUnit: {
          ...row.unassignedByUnit,
          [unitType]: rowCap == null ? left : Math.min(rowCap, left),
        },
      };
    });
  }, [unassignedByUnit, step2Rows, selections, assignments, assignedInUnit]);

  // The card reads in whatever unit the header dropdown is on, exactly as the table does.
  const detailRows = useMemo(
    () => detailFields?.(unitHeaderSelect.value, assignedInUnit) ?? EMPTY_LIST,
    [detailFields, unitHeaderSelect.value, assignedInUnit],
  );

  useEffect(() => {
    if (!open) return;
    setStep(SELECT_STEP);
    setSelectedIds(EMPTY_LIST);
    setPickedId(null);
    setAssignments({});
    setError(null);
    setSession((previous) => previous + 1);
    setDiscrepancyRow(null);
    onSearchChange?.('');
    onFilterChange?.({});
    // onSearchChange is the parent's setter; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  const handleSelectionChange = useCallback((rows, ids) => setSelectedIds(ids), []);
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);
  const handlePick = useCallback((id) => setPickedId(id), []);

  const handleAssign = useCallback((rowId, quantity) => {
    setAssignments((prev) => ({ ...prev, [rowId]: quantity }));
  }, []);

  const filterDefinitions = useMemo(() => buildContractFilterDefinitions(t), [t]);

  const handleFilterChange = useCallback((values) => onFilterChange?.(values), [onFilterChange]);

  // Server-side: `search` is a variable on the linkable-contracts query.
  const handleSearchChange = useCallback((value) => onSearchChange?.(value), [onSearchChange]);

  // Every row carries its own discrepancy items, so the popup needs no second read.
  const isShipmentRow = discrepancyRow != null && step === ASSIGN_STEP && isBulk;
  const discrepancySections = useMemo(() => {
    const unitType = discrepancyRow && resolveRowUnitType(discrepancyRow, selections);
    return isShipmentRow
      ? buildShipmentRowDiscrepancySections(t, discrepancyRow, unitType)
      : buildContractRowDiscrepancySections(t, discrepancyRow, unitType);
  }, [t, isShipmentRow, discrepancyRow, selections]);

  // Seeding a blank assignment per row is also what puts a shipment row on step 2.
  const goNext = useCallback(() => {
    const rows = isBulk ? shipments : contracts.filter((r) => selectedIds.includes(r.id));
    // Only the typed quantity is state; the unit follows the row's own selection.
    setAssignments(Object.fromEntries(rows.map((row) => [row.id, ''])));
    setError(null);
    setStep(ASSIGN_STEP);
  }, [isBulk, shipments, contracts, selectedIds]);

  const goBack = useCallback(() => {
    setStep(SELECT_STEP);
    setError(null);
  }, []);

  const handleSave = useCallback(() => {
    // The row's shown unit, so the payload matches the suffix beside the typed number.
    const entries = step2Rows
      .map((row) => {
        const unitType = resolveRowUnitType(row, selections);
        return {
          rowId: row.id,
          quantity: assignments[row.id],
          unitSymbol: rowUnitSymbol(row, unitType),
        };
      })
      .filter((entry) => {
        const amount = Number(entry.quantity);
        return entry.quantity !== '' && Number.isFinite(amount) && amount > 0;
      });

    if (entries.length === 0) {
      setError(t('LINK_ASSIGN_REQUIRED'));
      return;
    }
    const missingUnit = entries.find((entry) => !entry.unitSymbol);
    if (missingUnit) {
      setError(t('LINK_CONTRACTS_ASSIGN_UNIT_MISSING'));
      return;
    }
    // The cell already shows why; sending it would only come back as the server's toast.
    const overShipment = Object.entries(unassignedByUnit ?? {}).some(
      ([unitType, cap]) => cap != null && assignedInUnit(unitType) > cap,
    );
    if (overShipment) return;
    setError(null);
    onSave?.(entries, isBulk ? pickedId : null);
  }, [
    step2Rows,
    selections,
    assignments,
    unassignedByUnit,
    assignedInUnit,
    onSave,
    isBulk,
    pickedId,
    t,
  ]);

  const steps = useMemo(
    () => [{ label: t('LINK_CONTRACTS_STEP_SELECT') }, { label: t('LINK_STEP_ASSIGN') }],
    [t],
  );

  const step1Columns = useMemo(
    () =>
      buildLinkContractsStep1Columns(t, {
        headerSelect: unitHeaderSelect,
        selectMode: isBulk ? 'single' : 'multi',
        selectedId: pickedId,
        onSelect: handlePick,
        onOpenDiscrepancy: handleOpenDiscrepancy,
      }),
    [t, unitHeaderSelect, isBulk, pickedId, handlePick, handleOpenDiscrepancy],
  );

  const step2Columns = useMemo(() => {
    const build = isBulk ? buildLinkShipmentsStep2Columns : buildLinkContractsStep2Columns;
    return build(t, {
      headerSelect: unitHeaderSelect,
      assignments,
      onAssign: handleAssign,
      onOpenDiscrepancy: handleOpenDiscrepancy,
    });
  }, [t, isBulk, unitHeaderSelect, assignments, handleAssign, handleOpenDiscrepancy]);

  const emptyState = useMemo(() => ({ title: t('LINK_CONTRACTS_EMPTY') }), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  // The server ranks every candidate contract SUGGESTION or OTHER against the shipment(s).
  const step1Groups = useMemo(() => buildSuggestionGroups(t), [t]);
  const getStep1RowGroup = getSuggestionGroup;

  const canAdvance = isBulk ? pickedId != null : selectedIds.length > 0;

  const buttons = useMemo(() => {
    if (step === SELECT_STEP) {
      return [
        { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
        {
          label: t('COMMON_NEXT'),
          variant: ButtonVariants.primary,
          disabled: !canAdvance,
          onClick: goNext,
        },
      ];
    }
    return [
      { label: t('COMMON_PREVIOUS'), variant: ButtonVariants.ghost, onClick: goBack },
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        disabled: isSaving,
        loading: isSaving,
        onClick: handleSave,
      },
    ];
  }, [t, step, canAdvance, handleClose, goNext, goBack, handleSave, isSaving]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.large}
      title={title}
      buttons={buttons}
    >
      <div className={styles.body}>
        <Stepper steps={steps} activeStep={step} variant="flush" className={styles.stepper} />

        <SummaryCard heading={t('SECTION_GENERAL_DETAILS')} fields={detailRows} />

        {error ? <Alert variant={AlertVariants.danger}>{error}</Alert> : null}

        {/* TODO: use the library Tooltip once it ships. */}
        {!loading && step === SELECT_STEP && step1Rows.length === 0 ? (
          <Alert variant={AlertVariants.warning} title={t('LINK_CONTRACTS_NONE_TITLE')}>
            {t('LINK_CONTRACTS_NONE_BODY')}
          </Alert>
        ) : null}

        <UnitTypeContext.Provider value={contextValue}>
          {step === SELECT_STEP ? (
            <Table
              key={`select-${session}`}
              columns={step1Columns}
              data={step1Rows}
              loading={loading}
              getRowId={getRowId}
              searchable
              onSearchChange={handleSearchChange}
              filters={filterDefinitions}
              onFilterChange={handleFilterChange}
              selectable={!isBulk}
              onSelectionChange={handleSelectionChange}
              emptyState={emptyState}
              labels={tableLabels}
              groups={step1Groups}
              getRowGroup={getStep1RowGroup}
            />
          ) : (
            <>
              <div className={styles.unitField}>
                <Typography
                  as={TypographyElements.span}
                  variant={TypographyVariants.bodyXs}
                  color={TypographyColors.secondary}
                >
                  {t('LINK_ASSIGN_UNIT_LABEL')}
                </Typography>
                {/* The same set-every-row control the unit column's header uses. */}
                <Dropdown
                  value={unitHeaderSelect.value}
                  onChange={unitHeaderSelect.onChange}
                  options={unitHeaderSelect.options}
                  aria-label={t('LINK_ASSIGN_UNIT_LABEL')}
                />
              </div>
              <Table
                key={`assign-${session}`}
                columns={step2Columns}
                data={assignRows}
                getRowId={getRowId}
                showPagination={false}
                emptyState={emptyState}
                labels={tableLabels}
              />
            </>
          )}
        </UnitTypeContext.Provider>
      </div>

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={isShipmentRow ? discrepancyRow?.shipmentId : discrepancyRow?.contractId}
        discrepancies={discrepancyRow?.discrepancyItems}
        sections={discrepancySections}
      />
    </Modal>
  );
};
